import { cookies } from '@zing/shared/cookies/behavior'
import { getHubRoute } from '@zing/shared/handlers'
import { trans } from '@zing/shared/i18n'
import { changeMenu, changeVisibleRows, store } from '@zing/shared/store'
import { showModal } from '@zing/shared/components/popup'
import { EvaluationModal } from '@zing/shared/domain/feature/hub/evaluator/challenge/EvaluationModal'
import { ROUTES } from '@zing/shared/constants'

const getSolutionTitle = (hide: boolean, solution: any): string =>
  hide ? 'Empresa oculta' : solution.title

const handleVisibleRows = (rowIndexString: string, visibleValues: string[]): void => {
  const next = visibleValues.includes(rowIndexString)
    ? visibleValues.filter((v) => v !== rowIndexString)
    : [...visibleValues, rowIndexString]
  store.dispatch(changeVisibleRows(next))
}

export function createMenu(currentLocation?: string, feature?: string[]): any[] {
  const features = cookies.get('_perm')?.features
  const state = store.getState()
  const menuEvaluatorsState = state.menuEvaluatorsState
  const menuEvaluators = menuEvaluatorsState?.rows ?? []

  const menuEvaluatorsItems: any[] = [
    {
      title: trans('Voltar'),
      icon: ['fas', 'fa-reply'],
      path: getHubRoute(ROUTES.HUB.DASHBOARD),
      onClick: () => {
        store.dispatch(changeMenu(false))
        store.dispatch(changeVisibleRows([]))
      },
    },
    {
      title: trans('Minhas Avaliações'),
      icon: ['fas', 'clipboard-check'],
      active: currentLocation?.includes(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      path: getHubRoute(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      onClick: () => store.dispatch(changeVisibleRows([])),
    },
  ]

  menuEvaluators.forEach((row: any, rowIndex: number) => {
    const rowIndexString = rowIndex.toString()
    const evaluationStartDate = new Date(row.config.step.evaluationStartDate)
    const evaluationEndDate = new Date(row.config.step.closingDate)
    const today = new Date()
    const availableToEvaluate = evaluationStartDate <= today && today <= evaluationEndDate
    const solutions: any[] = []

    row.evaluation.steps.forEach((step: any) => {
      step.resolvers.forEach((resolver: any) => {
        resolver.solutions.forEach((solution: any) => {
          solutions.push({
            title: getSolutionTitle(row.config.hideIdeaTitle, solution),
            path: getHubRoute(
            ROUTES.HUB.EVALUATOR.EVALUATE_IDEA
              .replace(':challengeId', row.id)
              .replace(':stepId', step.id)
              .replace(':ideaId', solution.id)
          ),
            tooltipContent: getSolutionTitle(row.config.hideIdeaTitle, solution),
            grade: solution.points !== 0 ? solution.points : 0.0,
            fillPercentage: solution.evaluationFillPercentage,
            disabled: !availableToEvaluate,
            onGradeClick: () => {
              showModal('evaluationModal', {
                title: solution.title,
                component: <EvaluationModal disabled={!availableToEvaluate} />,
                buttons: [{ text: 'Salvar', value: 'labels.confirm.value' }, { text: 'Cancelar' }],
                data: { challengeId: row.id, stepId: step.id, ideaId: solution.id },
                isGetFromRedux: false,
              })
            },
          })
        })
      })
    })

    menuEvaluatorsItems.push({
      title: row.config.title,
      icon: ['fas', 'puzzle-piece'],
      path: window.location.pathname,
      active: currentLocation?.includes(`${row.id}`),
      isSubMenu: true,
      subMenuItems: solutions,
      accordionOpen: state.isEvaluationMenu.visibleRows.includes(rowIndexString),
      onClick: () => handleVisibleRows(rowIndexString, state.isEvaluationMenu.visibleRows),
    })
  })

  const companyMenu = [
    {
      title: trans('Visão geral'),
      icon: ['fas', 'fa-chart-bar'],
      active: currentLocation?.includes(ROUTES.HUB.DASHBOARD),
      path: getHubRoute(ROUTES.HUB.DASHBOARD),
    },
    {
      title: trans('Calendário'),
      icon: ['fas', 'calendar'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.CALENDAR),
      path: getHubRoute(ROUTES.HUB.COMPANY.CALENDAR),
    },
    {
      title: trans('Programas'),
      icon: ['fas', 'puzzle-piece'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.CHALLENGE_LIST),
      path: getHubRoute(ROUTES.HUB.COMPANY.CHALLENGE_LIST),
    },
    {
      title: trans('Ecossistemas'),
      icon: ['fas', 'user-group'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.ECOSYSTEM),
      path: getHubRoute(ROUTES.HUB.COMPANY.ECOSYSTEM),
    },
    {
      title: trans('Eventos'),
      icon: ['fas', 'ticket-simple'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.EVENTS_LIST),
      path: getHubRoute(ROUTES.HUB.COMPANY.EVENTS_LIST),
    },
    {
      title: trans('Gerenciar espaços'),
      icon: ['fas', 'door-open'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE),
    },
    {
      title: trans('Espaços reservados'),
      icon: ['fas', 'calendar-check'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED),
    },
    {
      title: trans('Gerenciar solicitações'),
      icon: ['fas', 'clipboard-list'],
      active: currentLocation?.includes(ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS),
    },
  ]

  const startupMenu = [
    {
      title: trans('Oportunidades'),
      icon: ['fas', 'rocket'],
      active: currentLocation?.includes(ROUTES.HUB.STARTUP.CHALLENGE_LIST),
      path: getHubRoute(ROUTES.HUB.STARTUP.CHALLENGE_LIST),
    },
    {
      title: trans('Minhas Inscrições'),
      icon: ['fas', 'lightbulb'],
      active: currentLocation?.includes(ROUTES.HUB.STARTUP.IDEA_LIST),
      path: getHubRoute(ROUTES.HUB.STARTUP.IDEA_LIST),
    },
  ]

  const evaluatorMenu = [
    {
      title: trans('Avaliar Ideias'),
      icon: ['fas', 'star-half-stroke'],
      active: currentLocation?.includes(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      path: getHubRoute(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      onClick: () => store.dispatch(changeMenu(true)),
    },
    {
      title: trans('Meus Convites'),
      icon: ['fas', 'bell'],
      active: currentLocation?.includes(ROUTES.HUB.EVALUATOR.INVITES),
      path: getHubRoute(ROUTES.HUB.EVALUATOR.INVITES),
      onClick: () => store.dispatch(changeMenu(true)),
    },
  ]

  const configMenu = [
    {
      title: trans('Configurações'),
      icon: ['fas', 'wrench'],
      active: currentLocation?.includes(ROUTES.HUB.USER_CONFIG),
      path: getHubRoute(ROUTES.HUB.USER_CONFIG),
    },
  ]

  if (state.isEvaluationMenu.isEvaluationMenu) {
    return menuEvaluatorsItems
  }

  const effectiveFeature =
    feature && feature.length > 0
      ? feature
      : (features?.map((f: any) => String(f.code)) ??
         (cookies.get('_permCodes') as string[] | undefined) ??
         [])

  let menuItems: any[] = []

  if (effectiveFeature.includes('challenge')) {
    menuItems = [...menuItems, ...companyMenu]
  }

  if (effectiveFeature.includes('idea')) {
    menuItems = [...menuItems, ...startupMenu]
  }

  if (effectiveFeature.includes('evaluation')) {
    menuItems = [...menuItems, ...evaluatorMenu]
  }

  menuItems = [...menuItems, ...configMenu]

  return menuItems
}
